'use client';

// Shared Region Select — register / profile (add-user-region D1/D2).
// Options только из `getRegions()` — без хардкода справочника (AC-REG-003 / FT-REG-01).

import { useEffect, useState } from 'react';
import { Select } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { getRegions } from '@/lib/api/regions-client';
import { PortalApiError } from '@/lib/api/errors';

export const REGION_HINT =
  'Поможет показывать новости, публикации и календарь для вашего региона';

const EMPTY_LABEL = 'Не указан';

export interface RegionSelectProps {
  value: string | null;
  onChange: (value: string | null) => void;
  error?: string;
  disabled?: boolean;
}

export function RegionSelect({ value, onChange, error, disabled }: RegionSelectProps) {
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getRegions();
        if (cancelled) return;
        setOptions(
          data.items.map((item) => ({
            value: item.id,
            label: item.region_name,
          })),
        );
      } catch (err) {
        if (cancelled) return;
        // Options error → toast; submit без региона допустим (D1/D2).
        const message =
          err instanceof PortalApiError ? err.message : 'Не удалось загрузить список регионов';
        notifications.show({
          title: 'Ошибка',
          message,
          color: 'red',
        });
        setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Select
      label="Регион"
      description={REGION_HINT}
      placeholder={EMPTY_LABEL}
      data={options}
      value={value}
      onChange={onChange}
      searchable
      clearable
      nothingFoundMessage="Ничего не найдено"
      error={error}
      disabled={disabled || loading}
      comboboxProps={{ withinPortal: true }}
    />
  );
}
